<template>
    <Card :title="$t('labels.custom_field')" v-if="customField">
        <form @submit.prevent="update">
            <div class="card-body">
                <div class="row">
                    <div class="col-lg-6">
                        <FormGroup :label="$t('labels.name')" required>
                            <Input
                                v-model="customField.title"
                            />
                        </FormGroup>
                    </div>
                    <div class="col-lg-6">
                        <FormGroup :label="$t('labels.type')">
                            <AppSelect
                                :model-value="type.key"
                                :selected-value="type.value"
                                :options="customFieldsOptions"
                                option-key="key"
                                option-value="value"
                                disabled
                            />
                        </FormGroup>
                    </div>
                    <div class="col-lg-12">
                        <FormGroup :label="$t('labels.description')">
                            <Input
                                v-model="customField.description"
                            />
                        </FormGroup>
                    </div>
                    <div class="col-lg-12" v-if="type.key === CustomFieldTypes.SELECT">
                        <FormGroup :label="$t('labels.select_items')">
                            <Tags
                                v-model="selectItems"
                                @keydown.enter.prevent
                            />
                        </FormGroup>
                    </div>
                    <div class="col-lg-12">
                        <FormGroup :label="$t('labels.default_value')">
                            <Input
                                v-if="type.key === CustomFieldTypes.STRING"
                                v-model="customField.default_value"
                            />
                            <Input
                                v-else-if="type.key === CustomFieldTypes.LINK"
                                v-model="customField.default_value"
                                placeholder="https://example.com"
                            />
                            <Input
                                v-else-if="type.key === CustomFieldTypes.NUMBER"
                                v-model="customField.default_value"
                                type="number"
                            />
                            <textarea
                                v-else-if="type.key === CustomFieldTypes.LONG_TEXT"
                                v-model="customField.default_value"
                                class="form-control"
                            />
                            <Checkbox
                                v-else-if="type.key === CustomFieldTypes.CHECKBOX"
                                v-model="customField.default_value"
                            />
                            <AppSelect
                                v-else-if="type.key === CustomFieldTypes.SELECT"
                                :model-value="customField.default_value"
                                :selected-value="customField.default_value"
                                @update:model-value="customField.default_value = $event.value"
                                :options="selectOptions"
                                option-key="value"
                                option-value="value"
                            />
                        </FormGroup>
                    </div>
                    <div class="col-lg-12">
                        <FormGroup :label="$t('labels.required')">
                            <Checkbox v-model="customField.required" />
                        </FormGroup>
                    </div>
                </div>
            </div>
            <div class="card-footer d-flex justify-content-between align-items-center">
                <div class="list-icons text-muted">
                    <span class="list-icons-item">
                        <i class="icon-plus3 mr-2"></i> {{ DateFormatter.formatDateTime(customField.created_at) }}
                    </span>

                    <span class="list-icons-item ml-3">
                        <i class="icon-pencil mr-2"></i> {{ DateFormatter.formatDateTime(customField.updated_at) }}
                    </span>
                </div>
                <div>
                    <AppButton color="light" @click="remove" class="mr-2">
                        <i class="icon-trash mr-2"></i>
                        {{ $t('buttons.delete') }}
                    </AppButton>
                    <AppButton color="indigo" type="submit">
                        <i class="icon-floppy-disk mr-2"></i>
                        {{ $t('buttons.edit') }}
                    </AppButton>
                </div>
            </div>
        </form>
    </Card>
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import AppSelect from "../../../../components/new/form/select/AppSelect.vue";
import Tags from "../../../../components/new/form/Tags.vue";
import Checkbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";

import { CustomFieldTypes } from "../../../enums/CustomFieldTypesEnum";
import { CustomFieldApi } from "../../../api/CustomFieldApi";
import MessageNotificator from "../../../../services/MessageNotificator";
import DateFormatter from "../../../../services/DateFormatter";

export default {
    name: 'CustomFieldEditPage',
    components: {
        Card, AppButton, FormGroup, Input, AppSelect, Tags, Checkbox
    },
    data() {
        return {
            DateFormatter,
            CustomFieldTypes,
            customFieldsOptions: [],
            type: {
                key: 'string',
                value: this.$t(`enum.custom_field.type.string`)
            },
            selectItems: '',
            customField: null
        }
    },
    computed: {
        selectOptions() {
            return this.selectItems
                .split(',')
                .map(item => item.trim())
                .filter(Boolean)
                .map(item => ({ value: item }));
        }
    },
    methods: {
        async fetchCustomField() {
            let customFields = await CustomFieldApi.findAll(this.$route.params.projectUuid, { models: ['product-item'], limit: 0 });
            let customField = customFields.find(field => field.uuid === this.$route.params.customFieldUuid);

            this.type = this.customFieldsOptions.find(option => option.key === customField.type) ?? this.type;
            this.selectItems = (customField.additional?.items ?? []).join(', ');

            this.customField = {
                title: customField.title,
                description: customField.description,
                default_value: customField.type === this.CustomFieldTypes.CHECKBOX
                    ? Boolean(customField.defaultValue)
                    : customField.defaultValue,
                required: Boolean(customField.required),
                uuid: customField.uuid,
                created_at: customField.createdAt,
                updated_at: customField.updatedAt
            };
        },
        async update() {
            await CustomFieldApi.edit(this.$route.params.projectUuid, {
                title: this.customField.title,
                description: this.customField.description,
                required: Boolean(this.customField.required),
                default_value: this.type.key === this.CustomFieldTypes.CHECKBOX && this.customField.default_value === ''
                    ? false
                    : this.customField.default_value,
                additional: {
                    items: this.type.key === this.CustomFieldTypes.SELECT
                        ? this.selectItems.split(',').map(item => item.trim()).filter(Boolean)
                        : []
                }
            }, this.customField.uuid);

            new MessageNotificator().success(this.$t('alerts.success'));
        },
        remove() {
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete', {
                value: this.$t('labels.custom_field')
            }), async () => {
                await CustomFieldApi.remove(this.$route.params.projectUuid, this.customField.uuid);
                this.$router.push({ name: 'CustomFieldsIndexPage' });
            });
        }
    },
    async mounted() {
        this.customFieldsOptions = Object.entries(this.CustomFieldTypes).map(([key, value]) => ({
            key: value,
            value: this.$t(`enum.custom_field.type.${value}`)
        }));

        await this.fetchCustomField();
    }
}
</script>
